/**
 * Supabase Client Configuration
 */

import { createClient } from '@supabase/supabase-js'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabasePublishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY

if (!supabaseUrl || !supabasePublishableKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient(supabaseUrl, supabasePublishableKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
})

// Auth helpers
const isStandaloneMode = () =>
  Capacitor.isNativePlatform()
  || window.matchMedia('(display-mode: standalone)').matches
  || (navigator as Navigator & { standalone?: boolean }).standalone === true

export const signInWithGoogle = async () => {
  const standalone = isStandaloneMode()
  const native = Capacitor.isNativePlatform()

  if (native) {
    // Native Capacitor: get the OAuth URL without redirecting, then open
    // it in the system browser via @capacitor/browser. The deep-link
    // handler will pick up the callback and exchange the code for a session.
    // redirectTo uses the custom URL scheme so Supabase redirects back to the
    // app with the PKCE code — the appUrlOpen listener exchanges it.
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: 'zrm://callback',
        skipBrowserRedirect: true,
      },
    })

    if (error) throw error
    if (!data.url) throw new Error('No OAuth URL returned')

    await Browser.open({ url: data.url })
  } else {
    // Web / PWA: get the OAuth URL and redirect in the same tab
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          // In PWA standalone mode there's no existing Google session, so the
          // hd hint causes Google to initiate the full SSO chain (e.g. Okta)
          // for the Workspace domain. Omitting it lets the user sign in with
          // any Google account directly, then Supabase handles domain checks.
          ...(standalone ? {} : { hd: 'zestdent.com' }),
          // 'select_account' also forces the full flow — skip in PWA
          ...(standalone ? {} : { prompt: 'select_account' }),
        },
        skipBrowserRedirect: true,
      },
    })

    if (error) throw error

    if (data?.url) {
      window.location.href = data.url
    }
  }
}

export const signOut = async () => {
  const { error } = await supabase.auth.signOut()
  if (error) {
    throw error
  }
}

export const getCurrentSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession()
  if (error) {
    throw error
  }
  return session
}

export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser()
  if (error) {
    throw error
  }
  return user
}

// Validate email domain
export const isValidDomain = (email: string): boolean => {
  return email.endsWith('@zestdent.com')
}

// Email OTP authentication
export const sendOtpToEmail = async (email: string) => {
  // Validate domain before sending OTP
  if (!isValidDomain(email)) {
    throw new Error('Only @zestdent.com email addresses are allowed')
  }

  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      shouldCreateUser: true,
    },
  })

  if (error) {
    throw error
  }
}

export const verifyEmailOtp = async (email: string, token: string) => {
  const { data, error } = await supabase.auth.verifyOtp({
    email,
    token,
    type: 'email',
  })

  if (error) {
    throw error
  }

  return data
}

// Password-based authentication (demo mode)
export const signInWithPassword = async (email: string, password: string) => {
  if (!isValidDomain(email)) {
    throw new Error('Only @zestdent.com email addresses are allowed')
  }
  const { data, error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) throw error
  return data
}
